export const MAX_RANGE_DAYS = 366;

export type DatePreset = 'today' | 'yesterday' | '7d' | 'month' | 'year';

export function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function toIso(year: number, monthIndex: number, day: number): string {
  return `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`;
}

export function parseIso(iso: string): { y: number; m: number; d: number } {
  const [y, m, d] = iso.split('-').map(Number);
  return { y, m: (m || 1) - 1, d: d || 1 };
}

export function addDaysIso(iso: string, days: number): string {
  const { y, m, d } = parseIso(iso);
  const dt = new Date(y, m, d + days);
  return toIso(dt.getFullYear(), dt.getMonth(), dt.getDate());
}

export function addMonthsIso(iso: string, delta: number): string {
  const { y, m, d } = parseIso(iso);
  const last = new Date(y, m + delta + 1, 0).getDate();
  const dt = new Date(y, m + delta, Math.min(d, last));
  return toIso(dt.getFullYear(), dt.getMonth(), dt.getDate());
}

export function addYearsIso(iso: string, delta: number): string {
  const { y, m, d } = parseIso(iso);
  const last = new Date(y + delta, m + 1, 0).getDate();
  return toIso(y + delta, m, Math.min(d, last));
}

export function startOfMonthIso(year: number, monthIndex: number): string {
  return toIso(year, monthIndex, 1);
}

export function endOfMonthIso(year: number, monthIndex: number): string {
  const last = new Date(year, monthIndex + 1, 0).getDate();
  return toIso(year, monthIndex, last);
}

export function startOfMonthFromIso(iso: string): string {
  const { y, m } = parseIso(iso);
  return startOfMonthIso(y, m);
}

export function startOfYearFromIso(iso: string): string {
  return `${parseIso(iso).y}-01-01`;
}

export function inclusiveDaySpan(from: string, to: string): number {
  const a = new Date(`${from}T00:00:00`);
  const b = new Date(`${to}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  const start = a <= b ? a : b;
  const end = a <= b ? b : a;
  return Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
}

export function orderedRange(a: string, b: string): { from: string; to: string } {
  return a <= b ? { from: a, to: b } : { from: b, to: a };
}

export function eachIso(from: string, to: string): string[] {
  const { from: start, to: end } = orderedRange(from, to);
  const out: string[] = [];
  let cursor = start;
  while (cursor <= end) {
    out.push(cursor);
    cursor = addDaysIso(cursor, 1);
  }
  return out;
}

export function formatChip(iso: string): string {
  const { y, m, d } = parseIso(iso);
  return new Date(y, m, d).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

export function formatShortRange(from: string, to: string): string {
  const a = parseIso(from);
  const b = parseIso(to);
  const fromDt = new Date(a.y, a.m, a.d);
  const toDt = new Date(b.y, b.m, b.d);
  const fromDay = fromDt.toLocaleDateString('en-GB', { day: 'numeric' });
  const toDay = toDt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  if (a.y === b.y && a.m === b.m) {
    return `${fromDay}–${toDay}`;
  }
  const fromFull = fromDt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  if (a.y === b.y) return `${fromFull} – ${toDay}`;
  return `${fromFull} ${a.y} – ${toDay} ${b.y}`;
}

export function activePreset(from: string, to: string, today: string): DatePreset | null {
  if (from === today && to === today) return 'today';
  const yest = addDaysIso(today, -1);
  if (from === yest && to === yest) return 'yesterday';
  if (from === addDaysIso(today, -6) && to === today) return '7d';
  if (from === startOfMonthFromIso(today) && to === today) return 'month';
  if (from === startOfYearFromIso(today) && to === today) return 'year';
  return null;
}

export function applyPresetRange(preset: DatePreset, today: string): { from: string; to: string } {
  if (preset === 'yesterday') {
    const y = addDaysIso(today, -1);
    return { from: y, to: y };
  }
  if (preset === '7d') return { from: addDaysIso(today, -6), to: today };
  if (preset === 'month') return { from: startOfMonthFromIso(today), to: today };
  if (preset === 'year') return { from: startOfYearFromIso(today), to: today };
  return { from: today, to: today };
}

export function previousComparisonRange(
  from: string,
  to: string,
  preset: DatePreset | null,
): { from: string; to: string } {
  if (preset === 'month' || preset === 'year') {
    const shift = preset === 'month' ? addMonthsIso : addYearsIso;
    return { from: shift(from, -1), to: shift(to, -1) };
  }
  const days = inclusiveDaySpan(from, to);
  const prevTo = addDaysIso(from, -1);
  const prevFrom = addDaysIso(prevTo, -(days - 1));
  return { from: prevFrom, to: prevTo };
}

export function previousRangeCaption(
  prevFrom: string,
  prevTo: string,
  preset: DatePreset | null,
): string {
  const span = formatShortRange(prevFrom, prevTo);
  if (preset === '7d') return `previous 7 days (${span})`;
  if (preset === 'today') return `yesterday`;
  if (preset === 'yesterday') return span;
  return span;
}
